'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Add type declarations for Google Analytics
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId);
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Conversion tracking functions
export function trackPurchase({
  transactionId,
  value,
  currency = 'USD',
  items,
  customerType,
}: {
  transactionId: string;
  value: number;
  currency?: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  customerType: 'new' | 'returning';
}) {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items,
    customer_type: customerType,
  });
}

export function trackSubscription({
  subscriptionId,
  tier,
  value,
  duration,
  isNew,
}: {
  subscriptionId: string;
  tier: string;
  value: number;
  duration: number;
  isNew: boolean;
}) {
  trackEvent('subscribe', {
    subscription_id: subscriptionId,
    subscription_tier: tier,
    value,
    duration_months: duration,
    is_new_subscription: isNew,
  });
}

export function trackRegistration({
  userId,
  method,
}: {
  userId: string;
  method: string;
}) {
  trackEvent('sign_up', {
    user_id: userId,
    method,
  });
}

export function trackLogin({
  userId,
  method,
}: {
  userId: string;
  method: string;
}) {
  trackEvent('login', {
    user_id: userId,
    method,
  });
}

export function trackCheckoutProgress({
  step,
  stepName,
  value,
}: {
  step: number;
  stepName: string;
  value?: number;
}) {
  trackEvent('checkout_progress', {
    checkout_step: step,
    checkout_step_name: stepName,
    value,
  });
}

export function trackPatientAcquisition({
  source,
  medium,
  campaign,
  cost,
}: {
  source?: string;
  medium?: string;
  campaign?: string;
  cost?: number;
}) {
  trackEvent('patient_acquisition', {
    acquisition_source: source,
    acquisition_medium: medium,
    acquisition_campaign: campaign,
    acquisition_cost: cost,
  });
}

export function trackLifetimeValue({
  userId,
  totalValue,
  subscriptionMonths,
  totalOrders,
}: {
  userId: string;
  totalValue: number;
  subscriptionMonths: number;
  totalOrders: number;
}) {
  trackEvent('customer_lifetime_value', {
    user_id: userId,
    lifetime_value: totalValue,
    subscription_months: subscriptionMonths,
    total_orders: totalOrders,
  });
}
